import { ModalManager, NotificationManager } from "@animech/ui";
import { ARProviderProps } from "@animech/ar";
import { ReactNode } from "react";
import { Entity } from "playcanvas";
import { ProductId } from "@animech/axiom-format/src/definitions/definition-bindings";
import { UiNodeQuerier } from "@animech/axiom-ui-builder";
import { SceneElementInfo } from "src/ui";
import {
	AnyWallEditingToolType,
	Localizer,
	Property,
	PropertyValue,
} from "src/common";
import {
	ConfigurationMode,
	CameraMode,
	FloorStyleName,
	WallColorName,
	WallSnapSettings,
	WallVisualizationSettings,
} from "src/scene";
import { ConfiguratorBridge, SceneObjectData, UnitSystem } from "src/core";
import { PostMessageHandler } from "src/postmessage-handler";
import { Root } from "@definitions";

export const uiViews = ["configurator", "productPicker"] as const;
export type UiView = (typeof uiViews)[number];
export const isUiView = (value: string): value is UiView => {
	return uiViews.indexOf(value as UiView) > -1;
};

export type UiLayout = "bottombar" | "sidebar";

export type UiSlot = UiView | "productInfo" | "cart" | "mobileConfigurator";

export type VisualizationData = {
	leftSidebarOpen: boolean;
	rightSidebarOpen: boolean;
	multiEditPenPanelOpen: boolean;
	designModalOpened: boolean;
	saveModalOpened: boolean;
	environmentSceneEnabled: boolean;
	cameraMode: CameraMode;
	cameraIsTransitioning: boolean;
	measurementsEnabled: boolean;
	unitSystem: UnitSystem;
	dragOperationActive: boolean;
	pickEntities(
		x: number,
		y: number,
		width?: number,
		height?: number,
	): Promise<Entity[]>;
	select(...entities: Entity[]): void;
	deleteSelected(): boolean;
	canDeleteSelected(): boolean;
	onToggleBottombar(open: boolean): void;
	onToggleSidebar(open: boolean): void;
	onToggleMultiEditPenPanel(open: boolean): void;
	onToggleEnvironmentScene?(enabled: boolean): void;
	onCanvasCreated(canvas: HTMLCanvasElement): void;
	onSetCameraMode(mode: CameraMode): void;
	onResetCameraFocus(focusOnSelection: boolean): void;
	onToggleMeasurements(): void;
	onSetUnitSystem(unitSystem: UnitSystem): void;
	onToggleDragOperationActive(active: boolean): void;
	onToggleOpenDesignModal(opened: boolean): void;
	onToggleSaveModal(opened: boolean): void;
	onOpenLanguageModal(): void;
};

export type CartData = {
	enabled: boolean;
	vatEnabled: boolean;
	estimateEnabled: boolean;
	onOpen(): void;
	onCheckout(): void;
	onImageCreated(image: HTMLImageElement): void;
};

export type MenuData = {
	onRestart(): void;
	onShare(): void;
	onExportToGLTFCallback(): void;
};

export type WallData = {
	wallSnapSettings: WallSnapSettings;
	activeWallEditingTool: AnyWallEditingToolType | undefined;
	wallVisualizationSettings: WallVisualizationSettings;
	createRoom(width: number, depth: number): boolean;
	addDoorToSelectedWall(): boolean;
	addWindowToSelectedWall(): boolean;
	onModifyWallSnapSettings(settings: Partial<WallSnapSettings>): void;
	onSetActiveWallEditingTool(type: AnyWallEditingToolType | undefined): void;
	onModifyWallVisualizationSettings(
		settings: Partial<WallVisualizationSettings>,
	): void;
};

export type LogoData = {
	configurator: LogoSources;
	overlay: LogoSources;
};

export type LogoSources = {
	desktop?: string;
	mobile?: string;
};

export type RoomData = {
	wallColor: WallColorName;
	floorStyle: FloorStyleName;
	setWallColor(name: WallColorName): void;
	setFloorStyle(name: FloorStyleName): void;
};

export type History = {
	onUndo(): void;
	canUndo(): boolean;
	onRedo(): void;
	canRedo(): boolean;
};

export type UiData = {
	cart: CartData;
	menu: MenuData;
	wall: WallData;
	visualization: VisualizationData;
	history: History;
	room: RoomData;
	modals: ModalManager;
	notifications: NotificationManager;
	logo: LogoData;
	view: UiView;
	loading: {
		startup: boolean;
		application: boolean;
		scene: boolean;
	};
	layout: UiLayout;
	localizer: Localizer;
	sceneElements: SceneElementInfo[];
	outOfStockWarningElements: SceneElementInfo[];
	plannerEnabled: boolean;
	configurationMode: ConfigurationMode;
	templatesEnabled: boolean;
	sceneObjects: SceneObjectData[];
	configuratorBridge: ConfiguratorBridge;
	uiNodeQuerier: UiNodeQuerier<Root>;
	emitPostMessage: PostMessageHandler["emit"];
	onSetConfigurationMode(mode: ConfigurationMode): void;
	renderUiSlot(slot: UiSlot): ReactNode;
	onCopy(text: string): void;
	getInStockDomainFromColorProperty: (
		colorProperty: Property,
	) => PropertyValue[];
	getInStockDomainFromProductId: (productId: ProductId) => PropertyValue[];
	getStockHiddenValues: (
		property: Property,
		fieldValues: unknown[],
	) => unknown[];
	arConfig?: Omit<ARProviderProps, "generatePageUrl">;
	getUrl: () => string;
};
